import { Modal } from '@evisort/eds/components/Modal/Modal';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import {
  deletePageSearchParam,
  getPageSearchQueryByKey,
} from '~/utils/searchQuery';

import { getUserName } from '../../utils/user';

const QueryString = 'welcomeMessage';

const onboardingIds = [
  OnboardingIdType.SplashPageCardOne,
  OnboardingIdType.SplashPageCardTwo,
  OnboardingIdType.SplashPageCardThree,
];

export function Welcome() {
  const intl = useIntl();
  const enableCanduOnboarding = useFlag(FlagType.Onboarding);

  const { navigate } = useRouting();

  const [showModal, setShowModal] = useState(false);
  useEffect(() => {
    const hasWelcomeMessage = getPageSearchQueryByKey<string>(QueryString, '');

    if (hasWelcomeMessage === 'true') {
      setShowModal(true);
    }
  }, []);

  const currentUser = useCurrentUser();
  const name = getUserName(currentUser, intl);
  const subdomain = currentUser?.client_config?.sub_domain;
  const title = intl.formatMessage(
    {
      id: 'Welcome.welcomeTitle',
      defaultMessage: 'Welcome to Evisort{name}!',
      description:
        'Welcome title with optional name. Example: name = ", John" or ""',
    },
    { name: name ? `, ${name}` : '' },
  );

  const children = enableCanduOnboarding ? (
    <PageLayout
      actions={[
        {
          level: 'primary',
          text: intl.formatMessage({
            id: 'Welcome.setupEvisort',
            defaultMessage: 'Set up Evisort',
            description: 'Button text for setting up Evisort',
          }),
          onClick: () => {
            setShowModal(false);
            deletePageSearchParam(QueryString);
            navigate(RoutePathType.Onboarding);
          },
        },
      ]}
      description={intl.formatMessage({
        id: 'Welcome.setupDescription',
        defaultMessage:
          "Finish setting up Evisort using our guided configuration wizard and you'll be up and running.",
        description: 'Description for Evisort setup process',
      })}
      title={title}
      subtitle={intl.formatMessage({
        id: 'Welcome.welcomeSubtitle',
        defaultMessage:
          'Get ready to experience intelligent contract management.',
        description: 'Welcome subtitle about contract management',
      })}
      welcome={{
        onboardingIds,
        enableLogo: true,
      }}
    />
  ) : (
    <Layout direction="column" spacing={2}>
      <Text>
        <FormattedMessage
          id="Welcome.workspaceInfo"
          defaultMessage="Your Evisort workspace is <strong>{subdomain}</strong>."
          description="Message showing the workspace subdomain. Example: subdomain = 'workday'"
          values={{
            subdomain,
            strong: (chunks) => <b>{chunks}</b>,
          }}
        />
      </Text>
      <Text>
        <FormattedMessage
          id="Welcome.loginInstructions"
          defaultMessage="In the future, you can log in by visiting evisort.com and logging in to your workspace, or by bookmarking the current page and navigating directly to <strong>{workspaceUrl}</strong> in your browser."
          description="Instructions for future login with workspace URL. Example: workspaceUrl = 'https://acme.evisort.com/'"
          values={{
            workspaceUrl: `${window.location.origin}/${subdomain}`,
            strong: (chunks) => <b>{chunks}</b>,
          }}
        />
      </Text>
    </Layout>
  );

  return (
    <>
      {
        <Modal
          size="full-page"
          isVisible={showModal && enableCanduOnboarding}
          onHide={() => {
            setShowModal(false);
            deletePageSearchParam(QueryString);
          }}
          header={{ title: '' }}
          footer={{
            actions: [
              {
                label: intl.formatMessage({
                  id: 'Welcome.skipSteps',
                  defaultMessage: 'Skip Steps',
                  description: 'Button text to skip onboarding steps',
                }),
                text: intl.formatMessage({
                  id: 'Welcome.skipSteps',
                  defaultMessage: 'Skip Steps',
                  description: 'Button text to skip onboarding steps',
                }),
                variant: 'tertiary',
                onClick: () => {
                  setShowModal(false);
                  deletePageSearchParam(QueryString);
                },
              },
            ],
          }}
        >
          {children}
        </Modal>
      }
      {
        <>
          <Modal
            isVisible={showModal && !enableCanduOnboarding}
            onHide={() => {
              setShowModal(false);
              deletePageSearchParam(QueryString);
            }}
            header={{ title }}
            footer={{
              actions: [
                {
                  label: intl.formatMessage({
                    id: 'Welcome.cancelButton',
                    defaultMessage: 'Cancel',
                    description: 'Cancel button text',
                  }),
                  text: intl.formatMessage({
                    id: 'Welcome.cancelButton',
                    defaultMessage: 'Cancel',
                    description: 'Cancel button text',
                  }),
                  variant: 'tertiary',
                  onClick: () => {
                    setShowModal(false);
                    deletePageSearchParam(QueryString);
                  },
                },
                {
                  label: intl.formatMessage({
                    id: 'Welcome.startWorking',
                    defaultMessage: 'Start working with Evisort',
                    description: 'Button text to start using Evisort',
                  }),
                  text: intl.formatMessage({
                    id: 'Welcome.startWorking',
                    defaultMessage: 'Start working with Evisort',
                    description: 'Button text to start using Evisort',
                  }),
                  variant: 'primary',
                  onClick: () => {
                    setShowModal(false);
                    deletePageSearchParam(QueryString);
                  },
                },
              ],
            }}
          >
            {children}
          </Modal>
        </>
      }
    </>
  );
}
